// src/services/azureHrService.js - HR Settings (Feiertage, Betriebsferien, Jahresarbeitszeit)

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "hrsettings",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const DEFAULT_TAGESARBEITSZEIT = 8.2;

// Create table if it doesn't exist. Nur einmal pro Session tatsaechlich
// anfragen (Tabelle existiert danach immer) - sonst schickt jeder Aufruf von
// getCorrections/getHrSettings/etc. erneut ein POST, das serverseitig ohnehin
// nur noch mit 409 (bereits vorhanden) beantwortet wird.
let tableEnsured = false;
const ensureTableExists = async () => {
  if (tableEnsured) return true;

  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/Tables?${AZURE_CONFIG.sasToken}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify({ TableName: AZURE_CONFIG.tableName }),
  });

  // 201 = created, 409 = already exists - both are fine
  if (response.status === 201 || response.status === 409) {
    tableEnsured = true;
    return true;
  }

  // If the response indicates the table already exists in error body, that's also fine
  if (!response.ok) {
    const text = await response.text();
    if (text.includes("TableAlreadyExists")) {
      tableEnsured = true;
      return true;
    }
    console.warn("Table creation response:", response.status, text);
  }

  tableEnsured = true;
  return true;
};

// Wrapper that ensures table exists before operations
const fetchWithTableCreation = async (url, options = {}) => {
  let response = await fetch(url, options);

  // If table doesn't exist (404 on table operations), create it and retry
  if (response.status === 404) {
    const text = await response.text();
    if (text.includes("TableNotFound") || text.includes("The table specified does not exist")) {
      await ensureTableExists();
      response = await fetch(url, options);
    }
  }

  return response;
};

// Calculate total weekdays (Mon-Fri) in a year
const countWeekdaysInYear = (year) => {
  let count = 0;
  const current = new Date(year, 0, 1);
  const end = new Date(year, 11, 31);
  while (current <= end) {
    const dow = current.getDay();
    if (dow >= 1 && dow <= 5) count++;
    current.setDate(current.getDate() + 1);
  }
  return count;
};

// Calculate Jahresarbeitszeit = (weekdays - Feiertage on weekdays) * Tagesarbeitszeit
export const computeJahresarbeitszeit = (year, feiertage = [], tagesarbeitszeit = DEFAULT_TAGESARBEITSZEIT) => {
  const totalWeekdays = countWeekdaysInYear(year);

  // Count Feiertage that fall on weekdays
  let feiertageOnWeekdays = 0;
  feiertage.forEach((ft) => {
    const date = new Date(ft.date);
    const dow = date.getDay();
    if (dow >= 1 && dow <= 5) feiertageOnWeekdays++;
  });

  const workingDays = totalWeekdays - feiertageOnWeekdays;
  return workingDays * tagesarbeitszeit;
};

// Get HR settings for a specific year
export const getHrSettings = async (year) => {
  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='hr',RowKey='${year}')?${AZURE_CONFIG.sasToken}`;

  const response = await fetchWithTableCreation(url, {
    headers: { Accept: "application/json;odata=nometadata" },
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Azure Table Error: ${response.status}`);
  }

  const item = await response.json();
  const feiertage = item.Feiertage ? JSON.parse(item.Feiertage) : [];
  const tagesarbeitszeit = item.Tagesarbeitszeit || DEFAULT_TAGESARBEITSZEIT;

  return {
    year: item.RowKey,
    tagesarbeitszeit,
    jahresarbeitszeit: computeJahresarbeitszeit(parseInt(item.RowKey), feiertage, tagesarbeitszeit),
    feiertage,
    betriebsferien: item.Betriebsferien ? JSON.parse(item.Betriebsferien) : [],
    ferientageByCategory: item.FerientageByCategory ? JSON.parse(item.FerientageByCategory) : {},
  };
};

// Get HR settings for all years
export const getAllHrSettings = async () => {
  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}()?${AZURE_CONFIG.sasToken}&$filter=PartitionKey eq 'hr'`;

  const response = await fetchWithTableCreation(url, {
    headers: { Accept: "application/json;odata=nometadata" },
  });

  if (!response.ok) {
    if (response.status === 404) return [];
    throw new Error(`Azure Table Error: ${response.status}`);
  }

  const data = await response.json();
  return (data.value || []).map((item) => {
    const feiertage = item.Feiertage ? JSON.parse(item.Feiertage) : [];
    const tagesarbeitszeit = item.Tagesarbeitszeit || DEFAULT_TAGESARBEITSZEIT;
    return {
      year: item.RowKey,
      tagesarbeitszeit,
      jahresarbeitszeit: computeJahresarbeitszeit(parseInt(item.RowKey), feiertage, tagesarbeitszeit),
      feiertage,
      betriebsferien: item.Betriebsferien ? JSON.parse(item.Betriebsferien) : [],
      ferientageByCategory: item.FerientageByCategory ? JSON.parse(item.FerientageByCategory) : {},
    };
  });
};

// Save HR settings for a year
export const saveHrSettings = async (year, settings) => {
  await ensureTableExists();

  const entity = {
    PartitionKey: "hr",
    RowKey: String(year),
    Tagesarbeitszeit: settings.tagesarbeitszeit || DEFAULT_TAGESARBEITSZEIT,
    Feiertage: JSON.stringify(settings.feiertage || []),
    Betriebsferien: JSON.stringify(settings.betriebsferien || []),
    FerientageByCategory: JSON.stringify(settings.ferientageByCategory || {}),
    UpdatedAt: new Date().toISOString(),
  };

  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='hr',RowKey='${year}')?${AZURE_CONFIG.sasToken}`;

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify(entity),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("HR Settings save error:", errorText);
    throw new Error(`Azure Table Error: ${response.status}`);
  }

  return true;
};

// Parse .ics file content and extract VEVENT entries as Feiertage
export const parseIcsFile = (icsContent, year) => {
  const events = [];
  const lines = icsContent.replace(/\r\n /g, "").split(/\r?\n/);

  let inEvent = false;
  let currentEvent = {};

  for (const line of lines) {
    if (line === "BEGIN:VEVENT") {
      inEvent = true;
      currentEvent = {};
    } else if (line === "END:VEVENT") {
      inEvent = false;
      if (currentEvent.date && currentEvent.date.startsWith(String(year))) {
        events.push({
          date: currentEvent.date,
          name: currentEvent.name || "Feiertag",
        });
      }
    } else if (inEvent) {
      if (line.startsWith("DTSTART")) {
        // Handle DTSTART;VALUE=DATE:20250101 or DTSTART:20250101T...
        const match = line.match(/(\d{4})(\d{2})(\d{2})/);
        if (match) {
          currentEvent.date = `${match[1]}-${match[2]}-${match[3]}`;
        }
      } else if (line.startsWith("SUMMARY:") || line.startsWith("SUMMARY;")) {
        const value = line.includes(":") ? line.split(":").slice(1).join(":") : "";
        currentEvent.name = value.trim();
      }
    }
  }

  return events;
};

// Utility: Calculate working days in a date range (Mon-Fri, excluding holidays)
// feiertage is now an array of {date, name} objects
export const calculateWorkingDays = (startDate, endDate, feiertage = [], betriebsferien = []) => {
  const holidayDates = new Set([
    ...feiertage.map((f) => (typeof f === "string" ? f : f.date)),
    ...betriebsferien,
  ]);
  let count = 0;
  const current = new Date(startDate);
  const end = new Date(endDate);

  while (current <= end) {
    const dayOfWeek = current.getDay();
    const dateStr = current.toISOString().split("T")[0];
    if (dayOfWeek >= 1 && dayOfWeek <= 5 && !holidayDates.has(dateStr)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }

  return count;
};

// Get corrections for a person in a year
export const getCorrections = async (teamId, year) => {
  await ensureTableExists();
  const filter = `PartitionKey eq 'correction_${teamId}' and RowKey ge '${year}-01-01' and RowKey le '${year}-12-31'`;
  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}()?${AZURE_CONFIG.sasToken}&$filter=${encodeURIComponent(filter)}`;

  const response = await fetchWithTableCreation(url, {
    headers: { Accept: "application/json;odata=nometadata" },
  });

  if (!response.ok) {
    if (response.status === 404) return [];
    throw new Error(`Azure Table Error: ${response.status}`);
  }

  const data = await response.json();
  return (data.value || []).map((item) => ({
    date: item.RowKey,
    gleitzeitHours: item.GleitzeitHours || 0,
    holidayDays: item.HolidayDays || 0,
    note: item.Note || "",
  })).sort((a, b) => a.date.localeCompare(b.date));
};

// Save a correction for a person at a specific date
export const saveCorrection = async (teamId, date, gleitzeitHours, holidayDays, note = "") => {
  await ensureTableExists();

  const entity = {
    PartitionKey: `correction_${teamId}`,
    RowKey: date,
    GleitzeitHours: gleitzeitHours,
    HolidayDays: holidayDays,
    Note: note,
    UpdatedAt: new Date().toISOString(),
  };

  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='correction_${teamId}',RowKey='${date}')?${AZURE_CONFIG.sasToken}`;

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify(entity),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Correction save error:", errorText);
    throw new Error(`Azure Table Error: ${response.status}`);
  }

  return true;
};

// Delete a correction
export const deleteCorrection = async (teamId, date) => {
  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='correction_${teamId}',RowKey='${date}')?${AZURE_CONFIG.sasToken}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: { "If-Match": "*" },
  });

  if (!response.ok && response.status !== 404) {
    throw new Error(`Azure Table Error: ${response.status}`);
  }
  return true;
};

// Calculate personal holiday entitlement
// Takes into account both pensum and the proportion of the year worked
export const calculateHolidayEntitlement = (hrSettings, member, year, effectiveStart, effectiveEnd) => {
  if (!hrSettings || !hrSettings.ferientageByCategory) return 0;
  const kategorie = member.kategorie || "";
  const baseDays = hrSettings.ferientageByCategory[kategorie] || 0;
  const pensum = (member.pensum || 100) / 100;

  // Calculate proportion of year worked
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);

  const workStart = effectiveStart > yearStart ? effectiveStart : yearStart;
  const workEnd = effectiveEnd < yearEnd ? effectiveEnd : yearEnd;

  if (workStart > workEnd) return 0;

  // Calculate days in year and days worked
  const msPerDay = 24 * 60 * 60 * 1000;
  const yearDays = (yearEnd - yearStart) / msPerDay + 1;
  const workedDays = (workEnd - workStart) / msPerDay + 1;
  const yearProportion = workedDays / yearDays;

  // Holiday entitlement = baseDays * pensum * proportion of year worked
  return baseDays * pensum * yearProportion;
};

// ----- Ferien-Erkennung (mit isFullDay + "ferien in h") -----------------
const isFerienHourlyEntry = (entry) =>
  ((entry?.typeName) || "").toLowerCase().includes("ferien in h");

const isFerienFullDayEntry = (entry) => {
  const t = ((entry?.typeName) || "").toLowerCase();
  if (isFerienHourlyEntry(entry)) return false;
  return (
    entry?.isFullDay &&
    (t.includes("ferien") || t.includes("urlaub") || t.includes("vacation"))
  );
};

// Calculate Gleitzeit for a person, considering corrections and pensum history
// corrections: array of {date, gleitzeitHours, holidayDays}
// ferienEntries: array of timekeeping-Einträge (nur vergangene Ferien); wird intern ausgewertet
// salaryEntries: array aus getSalaryEntries (optional); wenn null/leer → member.pensum
export const calculateGleitzeit = (
  hrSettings,
  member,
  totalHoursWorked,
  corrections = [],
  ferienEntries = [],
  salaryEntries = null
) => {
  if (!hrSettings || !hrSettings.tagesarbeitszeit) {
    return null;
  }

  const year = parseInt(hrSettings.year);
  const today = new Date();
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);

  const personStart = member.start ? new Date(member.start) : yearStart;
  const personEnd = member.ende ? new Date(member.ende) : yearEnd;

  const effectiveStart = personStart > yearStart ? personStart : yearStart;
  const effectiveEnd = personEnd < yearEnd ? personEnd : yearEnd;
  const effectiveToday = today < effectiveEnd ? today : effectiveEnd;

  if (effectiveStart > effectiveEnd) return null;

  const tagesarbeitszeit = hrSettings.tagesarbeitszeit || DEFAULT_TAGESARBEITSZEIT;
  const betriebsferien = hrSettings.betriebsferien || [];
  const effectiveStartStr = effectiveStart.toISOString().split("T")[0];
  const effectiveEndStr = effectiveEnd.toISOString().split("T")[0];

  // Pensumshistorie: aus salaryEntries bauen, sonst Fallback auf member.pensum
  const pensumPeriods =
    Array.isArray(salaryEntries) && salaryEntries.length > 0
      ? buildPensumPeriods(salaryEntries, effectiveStartStr, effectiveEndStr)
      : [{ from: effectiveStartStr, to: effectiveEndStr, pensum: member.pensum ?? 100 }];

  // Find the most recent correction on or before today
  const todayStr = effectiveToday.toISOString().split("T")[0];
  const relevantCorrections = corrections.filter((c) => c.date <= todayStr);
  const latestCorrection = relevantCorrections.length > 0
    ? relevantCorrections[relevantCorrections.length - 1]
    : null;

  let gleitzeitBase = 0;
  let holidayBase = 0;
  let calcStartDate = effectiveStartStr;

  if (latestCorrection) {
    gleitzeitBase = latestCorrection.gleitzeitHours || 0;
    holidayBase = latestCorrection.holidayDays || 0;
    // Start calculating from the day AFTER the correction
    const corrDate = new Date(latestCorrection.date);
    corrDate.setDate(corrDate.getDate() + 1);
    calcStartDate = corrDate.toISOString().split("T")[0];
  }

  // Total working days (weekdays - Feiertage, includes office holidays) für die ganze Anstellung
  const totalWorkingDays = calculateWorkingDays(
    effectiveStartStr,
    effectiveEndStr,
    hrSettings.feiertage,
    []
  );

  // personalJahresarbeitszeit: Summe über alle Perioden (totalWorkingDays_p × tagesarbeitszeit × pensum_p)
  let personalJahresarbeitszeit = 0;
  for (const period of pensumPeriods) {
    const pFrom = period.from > effectiveStartStr ? period.from : effectiveStartStr;
    const pTo = period.to < effectiveEndStr ? period.to : effectiveEndStr;
    if (pFrom > pTo) continue;
    const totalWdPeriod = calculateWorkingDays(pFrom, pTo, hrSettings.feiertage, []);
    const pensumFraction = (period.pensum ?? 100) / 100;
    personalJahresarbeitszeit += totalWdPeriod * tagesarbeitszeit * pensumFraction;
  }

  // expectedHours: pro Periode mit Betriebsferien-Hochrechnung (taeglicheArbeitszeit_p)
  let expectedHours = 0;
  let elapsedWorkingDays = 0;
  for (const period of pensumPeriods) {
    const pFrom = period.from > calcStartDate ? period.from : calcStartDate;
    const pTo = period.to < todayStr ? period.to : todayStr;
    if (pFrom > pTo) continue;

    // Betriebsferien auf Perioden-Ebene herausrechnen (wie bisher, nur pro Periode)
    const totalWdPeriod = calculateWorkingDays(period.from, period.to, hrSettings.feiertage, []);
    const actualWdPeriod = calculateWorkingDays(period.from, period.to, hrSettings.feiertage, betriebsferien);
    const pensumFraction = (period.pensum ?? 100) / 100;
    const taeglicheArbeitszeitPeriod =
      actualWdPeriod > 0
        ? (totalWdPeriod * tagesarbeitszeit * pensumFraction) / actualWdPeriod
        : tagesarbeitszeit * pensumFraction;

    const elapsedWdPeriod = calculateWorkingDays(pFrom, pTo, hrSettings.feiertage, betriebsferien);
    expectedHours += taeglicheArbeitszeitPeriod * elapsedWdPeriod;
    elapsedWorkingDays += elapsedWdPeriod;
  }

  // Anzeige-Tagesarbeitszeit: aktuelles Pensum × Tagesarbeitszeit (keine Betriebsferien-Hochrechnung)
  const pensumToday = getPensumAtDate(pensumPeriods, todayStr);
  const taeglicheArbeitszeit = tagesarbeitszeit * pensumToday;

  // Ferien-Credit: pro Eintrag tagesarbeitszeit × pensum_am_Eintragsdatum (fullDay)
  // bzw. die tatsächlichen Stunden (hourly). Parallel dazu holidaysTaken (in Tagen) berechnen.
  const entries = Array.isArray(ferienEntries) ? ferienEntries : [];
  let holidayCreditHours = 0;
  let holidaysTakenCount = 0;
  for (const entry of entries) {
    if (isFerienHourlyEntry(entry)) {
      const hours = (entry.durationHours || 0) + (entry.durationMinutes || 0) / 60;
      holidayCreditHours += hours;
      holidaysTakenCount += tagesarbeitszeit > 0 ? hours / tagesarbeitszeit : 0;
    } else if (isFerienFullDayEntry(entry)) {
      const entryPensum = getPensumAtDate(pensumPeriods, entry.date);
      holidayCreditHours += tagesarbeitszeit * entryPensum;
      holidaysTakenCount += 1;
    }
  }

  const gleitzeit = gleitzeitBase + (totalHoursWorked - expectedHours) + holidayCreditHours;

  // Holiday calculation
  const holidayEntitlement = calculateHolidayEntitlement(hrSettings, member, year, effectiveStart, effectiveEnd);
  const totalHolidays = holidayBase + holidaysTakenCount;
  const holidaysRemaining = holidayEntitlement - totalHolidays;

  return {
    personalJahresarbeitszeit: personalJahresarbeitszeit.toFixed(1),
    taeglicheArbeitszeit: taeglicheArbeitszeit.toFixed(2),
    elapsedWorkingDays,
    totalWorkingDays,
    expectedHours: expectedHours.toFixed(1),
    totalHoursWorked: totalHoursWorked.toFixed(1),
    gleitzeit: gleitzeit.toFixed(1),
    holidayEntitlement: holidayEntitlement.toFixed(1),
    holidaysTaken: totalHolidays.toFixed(1),
    holidaysRemaining: holidaysRemaining.toFixed(1),
    latestCorrection,
  };
};

// Pensum history helpers (US-TEAM-006) -----------------------------------

const toIsoDateStr = (value) =>
  typeof value === "string" ? value : value.toISOString().split("T")[0];

const addDaysIso = (isoDate, n) => {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().split("T")[0];
};

// Build a lückenlose Pensum-Historie zwischen effectiveStart und effectiveEnd
// aus den Lohneinträgen einer Person. Jede Periode gilt von `from` bis einschliesslich `to`.
// Pensum in den Perioden ist 0–100 (wie in salaryEntries); Konsumenten wandeln ggf. in 0–1.
export const buildPensumPeriods = (salaryEntries, effectiveStart, effectiveEnd) => {
  const start = toIsoDateStr(effectiveStart);
  const end = toIsoDateStr(effectiveEnd);
  if (start > end) return [];

  const sorted = (salaryEntries || [])
    .filter((e) => e && e.abDatum)
    .slice()
    .sort((a, b) => (a.abDatum < b.abDatum ? -1 : 1));

  const getPensum = (entry) => (entry.pensum != null ? entry.pensum : 100);

  if (sorted.length === 0) {
    return [{ from: start, to: end, pensum: 100 }];
  }

  const periods = [];
  for (let i = 0; i < sorted.length; i++) {
    const entry = sorted[i];
    const next = sorted[i + 1];
    const entryStart = entry.abDatum > start ? entry.abDatum : start;
    const entryEnd = next
      ? (() => {
          const beforeNext = addDaysIso(next.abDatum, -1);
          return beforeNext < end ? beforeNext : end;
        })()
      : end;

    if (entryStart > end) continue;
    if (entryEnd < entryStart) continue;

    periods.push({ from: entryStart, to: entryEnd, pensum: getPensum(entry) });
  }

  // Liegt der erste Lohneintrag nach effectiveStart, gilt das Pensum des ersten
  // Eintrags rückwirkend bis zum Tag vor dem ersten Lohneintrag.
  if (periods.length === 0) {
    return [{ from: start, to: end, pensum: getPensum(sorted[0]) }];
  }
  if (periods[0].from > start) {
    periods.unshift({
      from: start,
      to: addDaysIso(periods[0].from, -1),
      pensum: getPensum(sorted[0]),
    });
  }

  return periods;
};

// Liefert das Pensum (Dezimalwert 0–1) am angegebenen Datum.
export const getPensumAtDate = (pensumPeriods, date) => {
  if (!pensumPeriods || pensumPeriods.length === 0) return 1;
  const dateStr = toIsoDateStr(date);

  for (const period of pensumPeriods) {
    if (dateStr >= period.from && dateStr <= period.to) {
      return (period.pensum || 100) / 100;
    }
  }

  // Datum vor erster Periode → Pensum der ersten Periode
  if (dateStr < pensumPeriods[0].from) {
    return (pensumPeriods[0].pensum || 100) / 100;
  }
  // Datum nach letzter Periode → Pensum der letzten Periode
  const last = pensumPeriods[pensumPeriods.length - 1];
  return (last.pensum || 100) / 100;
};

// Transfer year-end balances to new year as correction on Jan 1
export const transferYearEnd = async (teamId, fromYear, gleitzeitBalance, holidayBalance) => {
  const newYearDate = `${fromYear + 1}-01-01`;
  await saveCorrection(
    teamId,
    newYearDate,
    gleitzeitBalance,
    holidayBalance,
    `Übertrag aus ${fromYear}`
  );
};
